<script lang="ts">
    // Jet/Models/Types
    import { type ArtistPageHeader } from '~/jet/models';

    // Components
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import MotionVideo from '~/components/jet/Artwork/MotionVideo.svelte';
    import CloudButtons from '~/components/jet/buttons/CloudButtons.svelte';
    import ShareIcon from '~assets/icons/share.svg';

    // Shared Components
    import ArtistDetailHeader from '@amp/web-app-components/src/components/ArtistDetailHeader/ArtistDetailHeader.svelte';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { mediaQueries as mediaQueryStore } from '@amp/web-app-components/src/stores/media-query';

    // Utils
    import { shareLinkOrCopy } from '~/utils/share';
    import { getCommerce } from '~/utils/commerce';
    import { isPremiumSubscriber } from '~/utils/user';
    import { buildSrc } from '@amp/web-app-components/src/components/Artwork/utils/srcset';

    export let header: ArtistPageHeader;

    const i18n = getI18n();
    const { state: commerceState } = getCommerce();

    $: name = header.title;
    $: isRoomHeader = header.isRoomHeader;
    $: isInstrumentHeader = header.type === 'instrument';
    $: artwork = header.artwork;
    $: artworkLarge = isRoomHeader ? artwork : header.uberArtwork;
    $: videoArtwork = header.videoArtwork;
    $: startYear = header.startYear;
    $: endYear = header.endYear;
    $: shareButton = header.shareButton;
    $: isPremiumUser = isPremiumSubscriber(
        $commerceState?.user?.subscriberType,
    );

    $: subtitle = (() => {
        if (startYear) {
            const baseSubtitle = $i18n.t(
                'ASE.Web.MusicClassical.ContentA.Middot.ContentB',
                { contentA: header.subtitle, contentB: startYear },
            );

            if (endYear) {
                return $i18n.t(
                    'ASE.Web.MusicClassical.ContentA.Dash.ContentB',
                    { contentA: baseSubtitle, contentB: endYear },
                );
            }

            return baseSubtitle;
        }

        return header.subtitle;
    })();

    // Logic for switching between different types of artwork, depending on screen
    // size, page type, and what artwork is available. Note that this behavior is
    // different from Music, which always uses ellipse (facehole) art at xsmall.
    //
    // - Room headers always use full width "large" artwork.
    // - Artist headers always use full width artwork at xsmall, and then
    //   full width uber artwork above xsmall if available. If there is
    //   no uber artwork, artist will fall back to ellipse above xsmall.
    $: screenIsXSmall = $mediaQueryStore === 'xsmall';
    $: useLargeArtwork = isRoomHeader || (!!artworkLarge && !screenIsXSmall);
    $: useFullWidthArtwork = useLargeArtwork || (!!artwork && screenIsXSmall);

    $: largeArtworkUrl = (() => {
        const fileType = 'webp' as const;

        if (!isRoomHeader && artworkLarge) {
            const options = {
                width: 2400,
                height: 933,
                crop: 'ea' as const,
                quality: 60,
                fileType,
            };
            const largeUrl = buildSrc(artworkLarge.template, options, {});
            return largeUrl;
        } else if (isRoomHeader) {
            const options = {
                width: 2400,
                height: 720,
                crop: 'sr' as const,
                quality: 60,
                fileType,
            };
            const largeUrl = buildSrc(artworkLarge.template, options, {});
            return largeUrl;
        }
    })();

    // Placeholder color displayed while image is loading
    $: bgColor = (isRoomHeader ? artwork : artworkLarge)?.backgroundColor;
</script>

<div
    class="artist-header-container"
    class:is-room-header={isRoomHeader}
    class:is-xsmall-artist-header={!isRoomHeader && screenIsXSmall && !!artwork}
>
    <ArtistDetailHeader
        {name}
        {subtitle}
        hasArtwork={!!artwork}
        hasUberArtwork={false}
        hasVideoArtwork={!!videoArtwork}
        hasFullWidthArtwork={useFullWidthArtwork}
        showFavoriteButton={isPremiumUser}
        showShareButton={!!shareButton}
    >
        <MotionVideo
            {videoArtwork}
            artwork={null}
            alt={name}
            profile="artist-detail-header"
            itemKind="artistDetailHeaderLockup"
            viewportThreshold={0}
            videoCover={true}
            slot="video-artwork"
        />

        <!--
            Artist facehole artwork - used when viewport is larger than xsmall
            and no uber artwork is available. This is automatically hidden
            whenever artwork in the full width slot is used.
        -->
        <Artwork {artwork} profile="facehole-uber" slot="artwork-gradient" />
        <Artwork {artwork} profile="facehole-uber" slot="artwork" alt={name} />

        <svelte:fragment slot="full-width-artwork">
            {#if useLargeArtwork}
                <!-- Rooms and artist uber artwork -->
                <div
                    class="full-width-artwork"
                    class:full-width-artwork--artist={!isRoomHeader}
                    class:full-width-artwork--room={isRoomHeader}
                    style:--background-image="url({largeArtworkUrl})"
                    style:--background-color="#{bgColor}"
                />
            {:else if useFullWidthArtwork}
                <!-- Artist xsmall artwork -->
                <Artwork
                    {artwork}
                    profile="header-lockup"
                    forceFullWidth={false}
                />
            {/if}
        </svelte:fragment>

        <CloudButtons id={header.id} type="artist" slot="favorited-badge" />

        <svelte:fragment slot="context-menu">
            {#if !isInstrumentHeader}
                <ProductContextMenu
                    contextMenuAction={header?.contextMenuAction}
                />
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="share-button">
            {#if shareButton && (!header?.contextMenuAction || isInstrumentHeader)}
                <button
                    data-testid="share-button"
                    aria-label={shareButton.title}
                    class="share-button"
                    on:click={() =>
                        shareLinkOrCopy({ url: shareButton?.action?.url })}
                >
                    <ShareIcon />
                </button>
            {/if}
        </svelte:fragment>
    </ArtistDetailHeader>
</div>

<style lang="scss">
    @use 'sass:math';

    .artist-header-container {
        display: contents;

        :global(.artist-header--fixed::after),
        :global(.artist-header--full-width::after) {
            $fixed-header-gradient: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.4) 0%,
                rgba(0, 0, 0, 0) 100%
            );
            height: 225px;
            background: $fixed-header-gradient;

            @media (--range-xsmall-down) {
                top: initial;
            }
        }

        :global(.artist-header__name) {
            grid-row: 3;
            grid-column: span 2;
            font: var(--header-emphasized-serif);

            @media (--small) {
                grid-row: 2;
            }
        }

        :global(.artist-header__favorite-button),
        :global(.artist-header__context-menu),
        :global(.artist-header__share-button) {
            @media (--small) {
                grid-row: 2;
            }
        }

        :global(.artist-header__share-button) {
            margin-inline-start: 8px;
        }
    }

    .is-room-header {
        $fixed-artwork-room-header-ratio: math.div(10, 3);
        $room-header-height-xs: 240px;
        $room-header-gradient: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.28) 0%,
            rgba(0, 0, 0, 0) 100%
        );

        :global(.artist-header) {
            --artistHeaderHeightXs: #{$room-header-height-xs};
        }

        :global(.artist-header--fixed),
        :global(.artist-header--full-width) {
            --fixed-artwork-ratio-lg: #{$fixed-artwork-room-header-ratio};
            --fixed-artwork-ratio-sm: #{$fixed-artwork-room-header-ratio};
        }

        :global(.artist-header--fixed::after),
        :global(.artist-header--full-width::after) {
            height: 120px;
            background: $room-header-gradient;
        }
    }

    .is-xsmall-artist-header {
        --artwork-override-height: 100%;
        --artwork-override-object-fit: cover;
        --artwork-override-object-position: bottom;
        --fixed-artwork-ratio-sm: 1;
        --artistHeaderHeightXs: auto;

        // Fix the aspect ratio of of the header to be almost square
        :global(.artist-header) {
            aspect-ratio: 15 / 13;
        }
    }

    .share-button {
        background: var(--shareButtonBackground);
        border-radius: inherit;
        width: 100%;
        height: 100%;

        :global(svg) {
            width: 13px;
            transform: translateY(-1px);
        }
    }

    .full-width-artwork {
        width: 100%;
        height: 100%;
        background-color: var(--background-color);
        background-image: var(--background-image);
        background-size: cover;
    }

    .full-width-artwork--artist {
        background-position: top center;
    }

    .full-width-artwork--room {
        background-position: center;
    }
</style>
